import "./PhotoSlider.scss";
import React, { useRef, useState } from "react";

export interface IPhotoSliderData {
  photos: string[];
  width?: number;
  height?: number;
  onSlide?: (e?: any) => void;
  className?: string;
}

const PhotoSlider = ({
  photos,
  width,
  height,
  onSlide,
  className
}: IPhotoSliderData) => {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [loadedImages, setLoadedImages] = useState<boolean[]>([]);
  const ref = useRef<HTMLDivElement>(null);
  const wheelLockedUntil = useRef<number>(0);

  const handleWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    if (!ref.current) {
      return;
    }
    // A trackpad swipe is left to the browser: it scrolls the container
    // natively and scroll-snap settles it on a slide. Detecting that by axis
    // dominance alone is not enough - the momentum tail decays to fractional
    // deltas (deltaX 0.2 against a stray deltaY of -0.5), which reads as a
    // vertical wheel and used to page the slider backwards mid-gesture.
    // So page a slide only for a decisive, essentially vertical movement,
    // which is what a mouse wheel produces.
    if (Math.abs(event.deltaX) > 2 || Math.abs(event.deltaY) < 10) {
      return;
    }
    // A wheel gesture arrives as a burst of events; advance one slide per
    // gesture rather than one per event.
    const now = Date.now();
    if (now < wheelLockedUntil.current) {
      return;
    }
    wheelLockedUntil.current = now + 400;
    // Derive the current slide from the live scroll position rather than from
    // state: after a native scroll the state briefly lags, and stepping from a
    // stale index skips a slide.
    const step = event.deltaY > 0 ? 1 : -1;
    const current = Math.round(
      ref.current.scrollLeft / ref.current.clientWidth
    );
    const target = Math.max(0, Math.min(current + step, photos.length - 1));
    ref.current.scrollTo({
      behavior: "smooth",
      left: target * ref.current.clientWidth
    });
  };

  const handleScroll = () => {
    if (!ref.current || ref.current.clientWidth === 0) {
      return;
    }
    // Round rather than ceil: ceil kept reporting the slide being scrolled away
    // from when moving backwards, which desynced the counter badge.
    const index = Math.round(ref.current.scrollLeft / ref.current.clientWidth);
    if (index !== currentIndex) {
      setCurrentIndex(index);
      if (onSlide) {
        onSlide(index);
      }
    }
  };

  const loading = loadedImages?.length !== photos?.length;

  return (
    <div
      className={`${
        loading ? "loading-animation" : ""
      } photo-slider hide-scroll-bar${className ? " " + className : ""}`}
      onWheel={handleWheel}
      onScroll={handleScroll}
      ref={ref}
    >
      {photos.map((photo: string) => (
        <img
          key={photo}
          src={photo}
          alt={""}
          width={width}
          height={height}
          className={`${
            loading ? "opacity-0" : "opacity-1 appear-animation"
          } photo`}
          onLoad={() => setLoadedImages((prev) => [...prev, false])}
          onError={() => setLoadedImages((prev) => [...prev, false])}
        />
      ))}
    </div>
  );
};

export default PhotoSlider;
